import { useState, useRef } from 'react';

// Single source of truth for the full disclaimer. Used by:
//  - DisclaimerBanner (collapsed/expandable inline component)
//  - DisclaimerModal (hard-gate full-screen pop-up before write/read actions)
// Drafted with reference to user-platform disclaimer patterns used by Yelp,
// generic review-platform legal templates, and FTC endorsement guidelines.
export const DISCLAIMER_SECTIONS = [
  {
    title: '1. Nature of the Platform',
    body: 'RenterRep, LLC ("RenterRep") operates an online platform that hosts reviews of property management, property managers, and tenants submitted by independent third-party users. RenterRep does not author, generate, create, edit, fact-check, verify, endorse, recommend, approve, or represent any review, rating, comment, or other user-submitted content ("User Content") that appears on this platform. All User Content is the sole opinion and expression of the individual user who submitted it.',
  },
  {
    title: '2. No Verification of User Content',
    body: 'RenterRep does not independently verify the accuracy, truthfulness, completeness, currency, or reliability of any User Content — including, without limitation, the identity of reviewers, the description of events, ratings, or any factual claim made in a review. Any reliance you place on User Content is entirely at your own sole risk.',
  },
  {
    title: '3. Reviews Are Personal Opinions, Not Statements of Fact',
    body: 'Every review on RenterRep represents the personal opinion and experience of the individual who submitted it. Opinions expressed in reviews do not reflect the views, recommendations, or endorsements of RenterRep, its officers, employees, agents, owners, or affiliates. The presence of a profile, rating, or review on this platform is not — and shall not be construed as — an endorsement, recommendation, approval, or warranty by RenterRep of any property management, property manager, tenant, or property.',
  },
  {
    title: '4. You Assume All Risk',
    body: 'By accessing, reading, relying on, or acting upon any review or User Content found on RenterRep, you acknowledge and agree that you do so entirely at your own risk. You alone are responsible for any decision you make in connection with information found on this platform, including without limitation: entering into a lease, accepting or declining a tenant or property management, withholding a rental opportunity, terminating a tenancy, pursuing legal action, or initiating any business or personal relationship. RenterRep is not a party to any such decision and accepts no responsibility for its outcome.',
  },
  {
    title: '5. Reviews Are Not a Recommendation to Rent or Decline',
    body: 'No review on RenterRep — positive or negative — constitutes a recommendation by RenterRep that you should or should not rent to, rent from, do business with, or otherwise interact with any property management, property manager, or tenant. The decision is yours alone, made on the basis of your own judgment and independent investigation.',
  },
  {
    title: '6. Conduct Your Own Due Diligence',
    body: 'Reviews on RenterRep are not a substitute for independent investigation. Before entering into any property management-tenant relationship or rental transaction, you should conduct your own due diligence — including but not limited to: verifying identities, contacting prior property management or tenants directly, reviewing public records, obtaining credit and background reports through lawful channels, inspecting properties in person, and consulting qualified legal, financial, real-estate, or housing professionals where appropriate. RenterRep does not perform any of these functions on your behalf.',
  },
  {
    title: '7. No Guarantee of Outcome',
    body: 'A positive review does not guarantee a positive rental experience, and a negative review does not guarantee a negative one. People and circumstances change; individual experiences vary widely. RenterRep makes no representation, warranty, or guarantee that any property management, property manager, or tenant will behave consistently with the opinions expressed in their reviews, or that any decision you make based on a review will produce any particular outcome.',
  },
  {
    title: '8. No Professional Advice',
    body: 'Nothing on RenterRep constitutes legal, financial, tax, real-estate, fair-housing, or other professional advice. Reviews and any other User Content are provided for informational purposes only. Always consult appropriately qualified professionals before making any consequential rental, financial, or legal decision.',
  },
  {
    title: '9. Fair Housing Compliance',
    body: 'Federal, state, and local fair-housing laws prohibit discrimination on the basis of race, color, religion, sex, national origin, familial status, disability, sexual orientation, gender identity, and other protected characteristics. RenterRep strictly prohibits User Content that references, encourages, or facilitates discrimination on any such basis. You are solely responsible for ensuring that any rental decision you make complies with all applicable fair-housing laws.',
  },
  {
    title: '10. Section 230 Protection',
    body: 'RenterRep operates as an interactive computer service provider under Section 230 of the Communications Decency Act (47 U.S.C. § 230) and expressly reserves all protections afforded thereunder. RenterRep is not the publisher or speaker of any User Content provided by third parties.',
  },
  {
    title: '11. Reviewer Responsibility & Warranty',
    body: 'Users who submit reviews to RenterRep are solely responsible for the content of their submissions. By submitting a review you represent and warrant that: (a) your review is based on genuine, first-hand personal experience; (b) it contains no false or defamatory statements of fact; (c) you have not been paid or otherwise compensated by any third party to submit it; (d) it does not infringe the privacy, intellectual-property, or other rights of any person; and (e) it complies with all applicable laws.',
  },
  {
    title: '12. Limitation of Liability',
    body: 'TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, RENTERREP, ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUES, DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO: (i) ANY USER CONTENT; (ii) YOUR USE OF OR INABILITY TO USE THE PLATFORM; (iii) ANY DECISION YOU MAKE IN RELIANCE ON USER CONTENT; OR (iv) ANY ALLEGED HARM TO REPUTATION CAUSED BY ANY REVIEW.',
  },
  {
    title: '13. Indemnification',
    body: 'You agree to defend, indemnify, and hold harmless RenterRep and its officers, directors, employees, agents, and affiliates from and against any and all claims, demands, liabilities, damages, losses, costs, and expenses (including reasonable attorneys’ fees) arising out of or in connection with: (a) your use of the platform; (b) any User Content you submit; or (c) your violation of these terms or any applicable law.',
  },
  {
    title: '14. False or Malicious Reviews',
    body: 'RenterRep prohibits the knowing submission of false, malicious, defamatory, or fraudulent reviews. Users who submit such content may face account termination, removal of content, and personal legal liability under applicable defamation, libel, and consumer-protection laws. RenterRep reserves the right, but is not obligated, to remove any User Content that violates these standards.',
  },
  {
    title: '15. Dispute Resolution & Arbitration',
    body: 'Any dispute arising from or relating to your use of this platform shall be resolved exclusively through binding individual arbitration administered by the American Arbitration Association under its Consumer Arbitration Rules, in Los Angeles County, California. You expressly waive any right to a jury trial and any right to participate in a class action.',
  },
  {
    title: '16. Governing Law',
    body: 'These terms and your use of RenterRep are governed by and construed in accordance with the laws of the State of California, without regard to its conflict-of-laws principles. Venue and jurisdiction for any non-arbitrable matter lie exclusively in the state and federal courts located in Los Angeles County, California.',
  },
  {
    title: '17. Modifications',
    body: 'RenterRep reserves the right to modify these terms at any time. Material changes will be reflected in the disclaimer presented on the platform. Your continued use of RenterRep after such changes constitutes acceptance of the updated terms.',
  },
  {
    title: '18. Acknowledgment & Acceptance',
    body: 'By checking the box below, you acknowledge that you have read, understand, and agree to all of the terms set forth above. You confirm that you understand RenterRep does not endorse, verify, or guarantee any review, rating, or individual on this platform; that you are reading and acting upon User Content at your own sole risk; that you will conduct your own independent due diligence; and that you assume full responsibility for any decision you make based on information found here.',
  },
];

export function DisclaimerBanner() {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ background: '#FAF6EC', borderRadius: 10, padding: '12px 14px', marginBottom: 16, border: '1px solid rgba(184,137,58,0.22)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>⚖️</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#0F2C4E' }}>Legal Disclaimer</span>
        </div>
        <span style={{ fontSize: 12, color: '#6B6358' }}>{expanded ? 'Hide ▲' : 'Read ▼'}</span>
      </div>
      <p style={{ fontSize: 11, color: '#6B6358', marginTop: 6, lineHeight: 1.6 }}>
        RenterRep does not write, create, endorse, or represent any reviews on this platform. All reviews are the sole opinion of individual users. By reading or acting on any review, you accept the full terms below at your own risk.
      </p>
      {expanded && (
        <div style={{ marginTop: 10, fontSize: 11, color: '#6B6358', lineHeight: 1.8, borderTop: '1px solid rgba(184,137,58,0.22)', paddingTop: 10 }}>
          {DISCLAIMER_SECTIONS.map((s, i) => (
            <p key={i} style={{ marginBottom: 8 }}>
              <strong style={{ color: '#0F2C4E' }}>{s.title}</strong> {s.body}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

export function DisclaimerCheckbox({ checked, onChange }) {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const scrollRef = useRef(null);

  const handleScroll = (e) => {
    const t = e.currentTarget;
    if (t.scrollHeight - t.scrollTop - t.clientHeight < 12) {
      setScrolledToBottom(true);
    }
  };

  // If the disclaimer text is short enough that there's nothing to scroll
  // (rare on desktop, possible at huge zooms), flip the gate open on mount
  // so we don't trap the user. The scroll container will tell us via its
  // initial scrollHeight vs clientHeight.
  const checkInitialOverflow = (el) => {
    if (el && el.scrollHeight - el.clientHeight < 12) {
      setScrolledToBottom(true);
    }
  };

  return (
    <div style={{ background: '#FAF6EC', borderRadius: 10, padding: 14, border: '1px solid rgba(184,137,58,0.22)', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#0F2C4E' }}>⚖️ Terms &amp; Disclaimer</span>
        <span style={{ fontSize: 11, color: scrolledToBottom ? '#1A7F37' : '#B8893A', fontWeight: 600 }}>
          {scrolledToBottom ? '✓ Read' : 'Scroll to the bottom ↓'}
        </span>
      </div>
      <div
        ref={(el) => { scrollRef.current = el; checkInitialOverflow(el); }}
        onScroll={handleScroll}
        style={{
          fontSize: 11,
          color: '#6B6358',
          lineHeight: 1.8,
          marginBottom: 12,
          maxHeight: 220,
          overflowY: 'auto',
          padding: '10px 10px',
          background: '#F5EFE3',
          borderRadius: 8,
          border: `1px solid ${scrolledToBottom ? 'rgba(27,127,55,0.45)' : 'rgba(184,137,58,0.30)'}`,
          transition: 'border-color 0.2s',
        }}
      >
        {DISCLAIMER_SECTIONS.map((s, i) => (
          <p key={i} style={{ marginBottom: 6 }}>
            <strong style={{ color: '#0F2C4E' }}>{s.title}</strong> {s.body}
          </p>
        ))}
      </div>
      <label style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        cursor: scrolledToBottom ? 'pointer' : 'not-allowed',
        opacity: scrolledToBottom ? 1 : 0.55,
        userSelect: 'none',
      }}>
        <input
          type="checkbox"
          disabled={!scrolledToBottom}
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0, accentColor: '#0F2C4E' }}
        />
        <span style={{ fontSize: 12, color: '#343A40', lineHeight: 1.6 }}>
          I have read and agree to RenterRep's full Disclaimer above. I understand RenterRep does not endorse or verify any review and I assume full responsibility for any decision I make based on information found here.
        </span>
      </label>
    </div>
  );
}
